import { useState } from "react";
import { useHomework, type Homework } from "@/hooks/useLearning";
import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";

type Tab = "tumu" | "bekliyor" | "tamamlandi" | "gecikti";

const tabs: { key: Tab; label: string }[] = [
  { key: "tumu", label: "Tümü" },
  { key: "bekliyor", label: "Bekleyen" },
  { key: "tamamlandi", label: "Tamamlanan" },
  { key: "gecikti", label: "Geciken" },
];

const statusConfig: Record<Homework["status"], { label: string; badge: string }> = {
  bekliyor: { label: "Bekliyor", badge: "bg-secondary-100 text-secondary-800" },
  tamamlandi: { label: "Tamamlandı", badge: "bg-primary-100 text-primary-700" },
  gecikti: { label: "Gecikti", badge: "bg-red-50 text-red-600" },
};

export default function Odevler() {
  const { data, loading, error, reload, markDone } = useHomework();
  const [tab, setTab] = useState<Tab>("tumu");

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;

  const items = data ?? [];
  const filtered = items.filter((h) => (tab === "tumu" ? true : h.status === tab));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">Ödevler</h1>
          <p className="text-sm md:text-base text-foreground-600 mt-1">
            Öğretmenlerinin verdiği ödevler ve teslim durumları.
          </p>
        </div>
      </div>

      {/* Sekmeler */}
      <div className="inline-flex rounded-full bg-background-200/70 p-1">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              tab === t.key
                ? "bg-background-50 text-foreground-950 shadow-sm"
                : "text-foreground-600 hover:text-foreground-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {filtered.map((hw) => (
          <div key={hw.id} className="bg-background-50 rounded-xl border border-background-200 p-5">
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full">
                    {hw.subject}
                  </span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusConfig[hw.status].badge}`}>
                    {statusConfig[hw.status].label}
                  </span>
                </div>
                <h3 className="font-heading font-semibold text-base text-foreground-950">{hw.title}</h3>
                <p className="text-sm text-foreground-600 mt-1 leading-relaxed">{hw.description}</p>
                <p className="text-xs text-foreground-500 mt-2 flex items-center gap-1">
                  <i className="ri-user-line" aria-hidden="true"></i>
                  {hw.teacher}
                </p>
              </div>

              <div className="flex flex-col items-end gap-2 shrink-0">
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground-600">
                  <i className="ri-calendar-line" aria-hidden="true"></i>
                  {hw.dueDate}
                </span>
                {hw.status === "bekliyor" && (
                  <button
                    type="button"
                    onClick={() => markDone(hw.id)}
                    className="h-9 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap inline-flex items-center gap-1.5"
                  >
                    <i className="ri-check-line" aria-hidden="true"></i>
                    Tamamla
                  </button>
                )}
                {hw.status === "gecikti" && (
                  <button
                    type="button"
                    onClick={() => markDone(hw.id)}
                    className="h-9 px-4 rounded-md bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors whitespace-nowrap inline-flex items-center gap-1.5"
                  >
                    <i className="ri-check-line" aria-hidden="true"></i>
                    Tamamla
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-10 text-center">
            <p className="text-sm text-foreground-500">Bu durumda ödev bulunmuyor.</p>
          </div>
        )}
      </div>
    </div>
  );
}